/**
 * The single source of truth for "how long is this turn taking".
 *
 * Before this, a turn showed two unrelated readouts: a `Thinking … Ns`
 * counter covering the whole wait, and the tool card's raw `12316ms` — a
 * *component* of that same wait, in different units, with nothing saying so.
 * A user reasonably read them as two independent timers.
 *
 * One clock now runs for the turn. The label names what is happening; the
 * number is always elapsed-since-send, so the phases read as a progression
 * rather than as competing stopwatches.
 */
import type { SearchProviderId } from "./searchProviders";

export type TurnPhase =
  /** Query is out to the search provider (which also fetches pages). */
  | "searching"
  /** TinyFish Agent is browsing start URLs. */
  | "browsing"
  /** Waiting on the model, before any token has arrived. */
  | "thinking"
  /** Settled. The number becomes a total. */
  | "done";

/**
 * There is deliberately no `streaming` phase. Once tokens arrive the whole
 * readout is unmounted (the turn stops being "thinking"), and the arriving
 * text is the progress indicator — a counter beside it only competes for
 * attention. A phase that no caller can set is worse than no phase.
 */

/** Copy for a settled turn's total. Shared so it is stated in one place. */
export const ANSWERED_IN = "Answered in";

/** What the UI shows for a phase. `null` means render nothing. */
export interface PhaseLabel {
  readonly text: string;
  /** Elapsed is still climbing; false once the turn has settled. */
  readonly live: boolean;
}

/** Label for a phase. `done` reuses {@link ANSWERED_IN}. */
export function phaseLabel(
  phase: TurnPhase,
  provider: SearchProviderId | null,
): PhaseLabel | null {
  switch (phase) {
    case "searching":
      return {
        text: provider === "tinyfish" ? "Searching and reading pages" : "Searching",
        live: true,
      };
    case "browsing":
      return { text: "Browsing sites", live: true };
    case "thinking":
      return { text: "Thinking", live: true };
    case "done":
      return { text: ANSWERED_IN, live: false };
  }
}

/** Elapsed, as the one format used everywhere a duration is shown. */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0.0s";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const whole = Math.floor(s);
  return `${Math.floor(whole / 60)}m ${whole % 60}s`;
}

/** Server-reported split for a TinyFish Search+Fetch call. */
export interface SearchTiming {
  search_ms: number;
  fetch_ms: number;
  fetched: number;
  requested: number;
}

/**
 * One line explaining where a search turn's time went.
 *
 * The card used to show a single opaque `12316ms` with no way to tell a slow
 * search from slow page reads — the distinction that actually matters, since
 * page fetching is the variable cost.
 */
export function describeSearchTiming(t: SearchTiming): string {
  const search = formatElapsed(t.search_ms);
  if (t.requested === 0) return `search ${search}`;
  const read = formatElapsed(t.fetch_ms);
  const pages = t.fetched === 1 ? "1 page" : `${t.fetched} pages`;
  // Not necessarily timeouts: a page can also be unreachable, blocked,
  // paywalled, oversized or empty. "unread" is the honest word for a line
  // whose job is diagnosing where the time went.
  const missed =
    t.fetched < t.requested ? ` (${t.requested - t.fetched} unread)` : "";
  return `search ${search} · read ${pages} ${read}${missed}`;
}

/** Parse the `timing` block, tolerating its absence on an Exa response. */
export function parseSearchTiming(raw: unknown): SearchTiming | null {
  if (typeof raw !== "object" || raw === null) return null;
  const t = raw as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
  const search_ms = num(t.search_ms);
  const fetch_ms = num(t.fetch_ms);
  const fetched = num(t.fetched);
  const requested = num(t.requested);
  if (search_ms === null || fetch_ms === null) return null;
  return {
    search_ms,
    fetch_ms,
    fetched: fetched ?? 0,
    requested: requested ?? 0,
  };
}
